// Inline slip-duration pill for the deliverable name-cell sub-line.
// `late` (red) signals an active problem (past due, no submission);
// `submitted_late` (amber) records that the submission landed after the
// due date — the delivery has happened but it slipped.
//
// Mirrors the milestone SlipTag pattern (data-testid, classes, headline
// variant) so the two tracking surfaces speak the same vocabulary.
//
// The spoken phrasing is VISUALLY-HIDDEN TEXT, not an aria-label. ARIA
// prohibits a name on a generic element, which is what this role-less span
// is: the label is flagged by automated checks and dropped outright by
// VoiceOver, leaving the listener with the ▲ glyph the phrasing exists to
// replace. The visible run is hidden from assistive tech instead.

import { clsx } from "clsx";

export type DeliverableSlipTagVariant = "late" | "submitted_late";

export interface DeliverableSlipTagProps {
  variant: DeliverableSlipTagVariant;
  days: number;
  /** Headline form (`12 days late`) used inside summary strip cards.
   *  Inline form (`▲ 12d late` / `▲ submitted 12d late`) is the default
   *  for table rows. */
  headline?: boolean;
  /** When supplied, drives a longer-form `title` tooltip
   *  ("Due 10 Apr 2026 · 5 days late"). Hovering reveals the
   *  underlying dates without the user having to expand the row.
   *  Both `dueDate` and (for the submitted-late variant) `submittedAt`
   *  must be set for the tooltip to render — otherwise we skip the
   *  attribute entirely to avoid half-formed copy. */
  dueDate?: string;
  submittedAt?: string | null;
}

const VARIANT_CLASS: Record<DeliverableSlipTagVariant, string> = {
  late: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  submitted_late:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function renderText(
  variant: DeliverableSlipTagVariant,
  days: number,
  headline: boolean,
): string {
  if (headline) {
    return variant === "late"
      ? `${days} days late`
      : `submitted ${days} days late`;
  }
  return variant === "late"
    ? `▲ ${days}d late`
    : `▲ submitted ${days}d late`;
}

// Screen readers verbalise the visual ▲ glyph as "up-pointing triangle"
// (or worse on some platforms) which is meaningless to the listener.
// This is independent semantic phrasing the contract manager actually
// hears: "Overdue by 3 days" / "Submitted 3 days late".
function renderSpokenText(
  variant: DeliverableSlipTagVariant,
  days: number,
): string {
  const unit = days === 1 ? "day" : "days";
  return variant === "late"
    ? `Overdue by ${days} ${unit}`
    : `Submitted ${days} ${unit} late`;
}

// Format an ISO date (possibly with a time portion) as "10 Apr 2026"
// in en-GB locale so the tooltip reads naturally without locale drift
// across testing environments.
function formatTooltipDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

// Assemble the longer-form tooltip when dates are available. Returns
// null when key dates are missing so we skip the title attribute
// entirely (no half-formed "Due undefined · 5 days late").
function renderTooltip(
  variant: DeliverableSlipTagVariant,
  days: number,
  dueDate: string | undefined,
  submittedAt: string | null | undefined,
): string | null {
  if (!dueDate) return null;
  const unit = days === 1 ? "day" : "days";
  const due = formatTooltipDate(dueDate);
  if (variant === "late") {
    return `Due ${due} · ${days} ${unit} late`;
  }
  if (!submittedAt) return null;
  const submitted = formatTooltipDate(submittedAt);
  return `Due ${due} · submitted ${submitted} · ${days} ${unit} late`;
}

export default function DeliverableSlipTag({
  variant,
  days,
  headline = false,
  dueDate,
  submittedAt,
}: DeliverableSlipTagProps) {
  const text = renderText(variant, days, headline);
  const tooltip = renderTooltip(variant, days, dueDate, submittedAt);
  return (
    <span
      data-testid={`deliverable-slip-tag-${variant}`}
      data-variant={variant}
      {...(tooltip !== null ? { title: tooltip } : {})}
      className={clsx(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-medium",
        VARIANT_CLASS[variant],
      )}
    >
      <span aria-hidden="true">{text}</span>
      <span className="sr-only">{renderSpokenText(variant, days)}</span>
    </span>
  );
}
